import React, { useState } from 'react';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import { theme } from '../styles/theme';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { userAPI } from '../utils/api';

const UploadContainer = styled.div`
  display: grid;
  gap: ${theme.spacing.xl};
`;

const UploadCard = styled(Card)`
  text-align: center;
  padding: ${theme.spacing.xl};
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.isDragActive ? theme.colors.primary : 'rgba(0, 0, 0, 0.23)'};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.xl} 0;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background: ${props => props.isDragActive ? `${theme.colors.primary}10` : 'transparent'};
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}10;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const UploadText = styled.div`
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.md};
`;

const FileName = styled.div`
  color: ${theme.colors.text.primary};
  font-weight: 500;
  margin-top: ${theme.spacing.md};
`;

const Upload = () => {
  const [file, setFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      setFile(droppedFile);
    } else {
      toast.error('Please upload a CSV file');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast.error('Please upload a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csvData = e.target.result;
        await userAPI.uploadCsvData({ data: csvData });
        toast.success('File uploaded successfully');
        setFile(null);
      };
      reader.readAsText(file);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <UploadContainer>
      <h1>Upload Users</h1>
      
      <UploadCard>
        <h2>Upload CSV File</h2>
        <p>Upload a CSV file containing user information</p>
        
        <FileInput
          type="file"
          accept=".csv"
          id="file-input"
          onChange={handleFileSelect}
        />
        
        <DropZone
          isDragActive={isDragActive}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <UploadIcon>📁</UploadIcon>
          <UploadText>
            Drag and drop your CSV file here, or click to select
          </UploadText>
          {file && <FileName>{file.name}</FileName>}
        </DropZone>
        
        <Button
          size="large"
          onClick={handleUpload}
          disabled={!file || loading}
        >
          {loading ? 'Uploading...' : 'Upload File'}
        </Button>
      </UploadCard>
    </UploadContainer>
  );
};

export default Upload; 